import { useEffect, useState, useMemo } from "react";
import useAxiosInstance from "../../hooks/useAxios";
import FilterModal from "../../components/FilterModal";
import { Paginator } from "../../components/Paginator";
import { TableCustom } from "../../components/Table";
import { FaFilter } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { REPORT_LIST_TABLE_KEYS } from "../../helper";
import { toast } from "react-toastify";

const TABS = [
  { key: "all", label: "All Reports" },
  { key: true, label: "Paused Reports" },
];

const formatDisplayDate = (dateString) => {
  if (!dateString) return "N/A";
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return "Invalid Date";
    }
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
    });
  } catch (error) {
    return "Invalid Date";
  }
};


const ReportManager = () => {
  const axiosInstance = useAxiosInstance();
  const history = useNavigate();

  const [page, setPage] = useState(1);
  const [intermediatePageNumber, setIntermediatePageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [loading, setLoading] = useState(true); // Set initial loading to true
  const [actionLoading, setActionLoading] = useState(false);
  const [sortOptions, setSortOptions] = useState({ sort_field: 'last_updated', sort_order: 'desc' });
  const [serverData, setServerData] = useState({
    reports_list: [],
    user_summary: { num_pages: 1, total_reports: 0 },
  });
  const [selectedRows, setSelectedRows] = useState({});
  const [modal, setModal] = useState(false);
  const [selectedOrgList, setSelectedOrgList] = useState({});
  const [selectedUserList, setSelectedUserList] = useState({});
  const [filterTimeRange, setFilterTimeRange] = useState({
    startDate: null,
    endDate: null,
  });
  const [activeTab, setActiveTab] = useState("all");

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    return new Date(dateStr).toISOString().split('T')[0];
  };

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);

    const params = {
      required_items: 'reports_list,user_summary',
      start_date: formatDate(filterTimeRange.startDate),
      end_date: formatDate(filterTimeRange.endDate),
      view_report_page: page,
      view_report_size: pageSize,
      sort_field: sortOptions.sort_field,
      sort_order: sortOptions.sort_order,
      org_ids: Object.keys(selectedOrgList).filter(id => selectedOrgList[id]).join(','),
      user_id: Object.keys(selectedUserList).filter(id => selectedUserList[id]).join(','),
      view_report_is_paused : activeTab
    };

    Object.keys(params).forEach(key => (params[key] == null || params[key] === '') && delete params[key]);

    axiosInstance
      .get("/admin/wrapper_admin_api/", {
        signal: controller.signal,
        params: params,
      })
      .then((res) => {
        const data = res.data || {};
        setServerData({
          reports_list: data.reports_list || [],
          user_summary: data.user_summary || { num_pages: 1, total_reports: 0 },
        });
        setIntermediatePageNumber(page);
      })
      .catch((err) => {
        if (err.name !== "CanceledError") {
          toast.error("Failed to fetch reports");
        }
      })
      .finally(() => {
        setLoading(false);
      });

    return () => controller.abort();
  }, [page, pageSize, filterTimeRange, sortOptions, selectedOrgList, selectedUserList, actionLoading, activeTab]);

  const reportsFromApi = useMemo(() => serverData.reports_list || [], [serverData]);

  const rowsToDisplay = useMemo(() => {
    return reportsFromApi
  }, [reportsFromApi, activeTab]);

  const paginatorData = useMemo(() => {
    const totalCount = (serverData.user_summary?.active_reports || 0) + (serverData.user_summary?.deleted_reports || 0);
    return {
      total_pages: Math.ceil(totalCount / pageSize) || 1,
      count: totalCount,
      current_page: page,
    };
  }, [serverData, page, pageSize]);

  const selectedIds = useMemo(() => Object.keys(selectedRows).filter(id => selectedRows[id]), [selectedRows]);
  const numSelected = selectedIds.length;
  const allOnPageSelected = rowsToDisplay.length > 0 && numSelected === rowsToDisplay.length;

  const toggleRow = (id) => setSelectedRows((prev) => ({ ...prev, [id]: !prev[id] }));

  const handleTabChange = (tabKey) => {
    setLoading(true);
    setActiveTab(tabKey);
    setPage(1);
    setSelectedRows({});
  };

  const handleToggleSelectAll = () => {
    if (allOnPageSelected) {
      setSelectedRows({});
    } else {
      const newSelectedRows = rowsToDisplay.reduce((acc, row) => {
        acc[row.id] = true;
        return acc;
      }, {});
      setSelectedRows(newSelectedRows);
    }
  };

  const handlePause = async () => {
    if (numSelected === 0) return;
    setActionLoading(true);
    try {
      await axiosInstance.post('/accounts/change_report_pause_status/', { report_ids: selectedIds,mark_status:'pause' });
      toast.success(`${numSelected} report(s) have been paused successfully.`);
      setSelectedRows({});
    } catch (error) {
      toast.error("Failed to pause reports. Please try again.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleStart = async () => {
    if (numSelected === 0) return;
    setActionLoading(true);
    try {
      await axiosInstance.post('/accounts/change_report_pause_status/', { report_ids: selectedIds,mark_status:'resume' });
      toast.success(`${numSelected} report(s) have been started successfully.`);
      setSelectedRows({});
    } catch (error) {
      toast.error("Failed to start reports. Please try again.");
    } finally {
      setActionLoading(false);
    }
  };

  const columnsWithCheckbox = useMemo(() => {
    return REPORT_LIST_TABLE_KEYS.map(col => {
      if (col.key === 'checkbox') {
        return {
          ...col,
          Cell: ({ row }) => (
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              checked={!!selectedRows[row.id]}
              onChange={() => toggleRow(row.id)}
            />
          ),
        };
      }
      return col;
    });
  }, [selectedRows, rowsToDisplay]);

  return (
    <div className="p-2 overflow-x-auto bg-slate-100 mt-4 rounded-xl">
      <div className="flex justify-between items-center mb-4">
        <div className="flex gap-x-2">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              className={`px-4 py-2 rounded-lg font-medium border-b-2 transition
                ${activeTab === tab.key
                  ? "border-black text-black bg-white shadow-sm"
                  : "border-transparent text-gray-500 bg-slate-200 hover:bg-slate-300"
                }`}
              onClick={() => handleTabChange(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-x-2">
          <button
            onClick={handleToggleSelectAll}
            disabled={rowsToDisplay.length === 0 || loading || actionLoading}
            className="text-sm bg-black cursor-pointer py-1 px-3 rounded-lg text-white font-medium disabled:bg-gray-400 disabled:cursor-not-allowed transition"
          >
            {allOnPageSelected ? 'Deselect All' : 'Select All'}
          </button>

          {activeTab === 'all' && (
            <button
              onClick={handlePause}
              disabled={numSelected === 0 || actionLoading}
              className="text-sm bg-black py-1 px-3 rounded-lg text-white font-medium flex items-center gap-2 disabled:bg-gray-400 disabled:cursor-not-allowed transition"
            >
              {actionLoading ? 'Processing...' : (
                <>
                  Pause
                  {numSelected > 0 && (
                    <span className="bg-white text-black text-xs font-bold rounded-full px-2 py-0.5">
                      {numSelected}
                    </span>
                  )}
                </>
              )}
            </button>
          )}

          {activeTab === true && (
            <button
              onClick={handleStart}
              disabled={numSelected === 0 || actionLoading}
              className="text-sm bg-black py-1 px-3 rounded-lg text-white font-medium flex items-center gap-2 disabled:bg-gray-400 disabled:cursor-not-allowed transition"
            >
              {actionLoading ? 'Processing...' : (
                <>
                  Start
                  {numSelected > 0 && (
                    <span className="bg-white text-black text-xs font-bold rounded-full px-2 py-0.5">
                      {numSelected}
                    </span>
                  )}
                </>
              )}
            </button>
          )}

          <button onClick={() => setModal(true)} className="p-2 bg-white rounded-lg border border-gray-300 hover:bg-gray-50">
            <FaFilter />
          </button>
        </div>
      </div>
      <FilterModal
        filterTimeRange={filterTimeRange}
        modal={modal}
        selectedOrgList={selectedOrgList}
        selectedUserList={selectedUserList}
        setFilterTimeRange={setFilterTimeRange}
        setModal={setModal}
        setReportListPageNumber={setPage}
        setSelectedOrgList={setSelectedOrgList}
        setSelectedUserList={setSelectedUserList}
      />
      <TableCustom
        columns={columnsWithCheckbox}
        data={{ ...serverData, data: rowsToDisplay }}
        loading={loading}
        handleDelete={null}
      />
      <Paginator
        data={paginatorData}
        loading={loading}
        setPage={setPage}
        setIntermidiatePageNumber={setIntermediatePageNumber}
        intermidiatePageNumber={intermediatePageNumber}
        setPageSize={setPageSize}
        pageSize={pageSize}
      />
    </div>
  );
};

export default ReportManager;